import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
} from '@mui/material';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';

// ─── ConfirmationDialog ────────────────────────────────────────────────────────
const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  titleIcon,
  titleText,
  contentText,
  cancelText = 'Cancelar',
  confirmText,
  confirmColor = 'primary',
  confirmIcon,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{ sx: { borderRadius: 3, minWidth: 360, maxWidth: 440 } }}
  >
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 2.5, pb: 1.5 }}>
      {titleIcon}
      <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
        {titleText}
      </Typography>
    </DialogTitle>
    <Divider />
    <DialogContent sx={{ pt: 2.5, pb: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
        {contentText}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
      <Button
        onClick={onClose}
        startIcon={<CloseIcon />}
        sx={{ borderRadius: 2, textTransform: 'none' }}
      >
        {cancelText}
      </Button>
      <Button
        onClick={onConfirm}
        color={confirmColor}
        variant="contained"
        startIcon={confirmIcon}
        disableElevation
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
      >
        {confirmText}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Papelera ──────────────────────────────────────────────────────────────────
const Papelera = () => {
  const { language, currentUser } = useAuth();

  const translations = useMemo(() => ({
    en: {
      title: 'Recycle Bin',
      subtitle: 'Deleted surveys are not removed automatically',
      empty: 'The recycle bin is empty',
      emptySubtitle: 'Deleted surveys will appear here',
      na: 'N/A',
      noTitle: 'Untitled',
      noDescription: 'No description',
      questions: (count) => `${count} question${count !== 1 ? 's' : ''}`,
      deletedOn: 'Deleted',
      recover: 'Recover',
      deletePermanently: 'Delete permanently',
      confirmRecover: 'Confirm Recovery',
      confirmDeletePermanent: 'Confirm Permanent Deletion',
      sureRecover: (title) =>
        `Are you sure you want to recover the survey "${title}"? It will be available again in your dashboard.`,
      sureDeletePermanent: (title) =>
        `Are you ABSOLUTELY sure you want to permanently delete "${title}"? This action CANNOT be undone.`,
      cancel: 'Cancel',
      recoverBtn: 'Recover',
      deletePermanentBtn: 'Delete Forever',
      surveyRecovered: 'Survey recovered successfully',
      surveyDeletedPermanent: 'Survey permanently deleted',
      errorRecover: 'Error recovering survey',
      errorDeletePermanent: 'Error deleting survey permanently',
      errorLoading: 'Error loading recycle bin',
      surveysCount: (n) => `${n} survey${n !== 1 ? 's' : ''}`,
    },
    es: {
      title: 'Papelera',
      subtitle: 'Los elementos eliminados no se borran automáticamente',
      empty: 'La papelera está vacía',
      emptySubtitle: 'Las encuestas eliminadas aparecerán aquí',
      na: 'N/A',
      noTitle: 'Sin título',
      noDescription: 'Sin descripción',
      questions: (count) => `${count} pregunta${count !== 1 ? 's' : ''}`,
      deletedOn: 'Eliminada el',
      recover: 'Recuperar',
      deletePermanently: 'Eliminar definitivamente',
      confirmRecover: 'Confirmar recuperación',
      confirmDeletePermanent: 'Confirmar eliminación definitiva',
      sureRecover: (title) =>
        `¿Estás seguro de que deseas recuperar la encuesta "${title}"? Volverá a estar disponible en tu panel.`,
      sureDeletePermanent: (title) =>
        `¿Estás ABSOLUTAMENTE seguro de que deseas eliminar definitivamente "${title}"? Esta acción NO se puede deshacer.`,
      cancel: 'Cancelar',
      recoverBtn: 'Recuperar',
      deletePermanentBtn: 'Eliminar para siempre',
      surveyRecovered: 'Encuesta recuperada correctamente',
      surveyDeletedPermanent: 'Encuesta eliminada definitivamente',
      errorRecover: 'Error al recuperar la encuesta',
      errorDeletePermanent: 'Error al eliminar definitivamente la encuesta',
      errorLoading: 'Error al cargar la papelera',
      surveysCount: (n) => `${n} encuesta${n !== 1 ? 's' : ''}`,
    },
  }), []);

  const lang = language === 'es' ? 'es' : 'en';
  const locale = lang === 'en' ? 'en-US' : 'es-ES';

  const t = useCallback((key, ...args) => {
    const value = translations[lang][key];
    if (typeof value === 'function') return value(...args);
    return value;
  }, [lang, translations]);

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [recoverDialog, setRecoverDialog] = useState(false);
  const [deletePermanentDialog, setDeletePermanentDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!currentUser) { setLoading(false); return; }

    const q = query(
      collection(db, 'surveys'),
      where('createdBy', '==', currentUser.uid),
      where('isDelete', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setSurveys(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching trash:', err);
        setError(t('errorLoading'));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, t]);

  const formatDate = (timestamp) => {
    if (!timestamp) return t('na');
    try {
      let date;
      if (timestamp instanceof Date) date = timestamp;
      else if (timestamp.toDate) date = timestamp.toDate();
      else if (timestamp.seconds !== undefined) date = new Date(timestamp.seconds * 1000);
      else date = new Date(timestamp);
      return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return t('na'); }
  };

  const getQuestionCount = (questions) => Object.keys(questions || {}).length;

  const handleRecoverClick = (survey) => { setSelectedSurvey(survey); setRecoverDialog(true); };
  const handleDeletePermanentClick = (survey) => { setSelectedSurvey(survey); setDeletePermanentDialog(true); };

  const handleRecoverConfirm = async () => {
    if (!selectedSurvey) return;
    try {
      await updateDoc(doc(db, 'surveys', selectedSurvey.id), { isDelete: false });
      setSnackbar({ open: true, message: t('surveyRecovered'), severity: 'success' });
    } catch (err) {
      console.error('Error recovering survey:', err);
      setSnackbar({ open: true, message: t('errorRecover'), severity: 'error' });
    }
    setRecoverDialog(false);
    setSelectedSurvey(null);
  };

  const handleDeletePermanentConfirm = async () => {
    if (!selectedSurvey) return;
    try {
      await deleteDoc(doc(db, 'surveys', selectedSurvey.id));
      setSnackbar({ open: true, message: t('surveyDeletedPermanent'), severity: 'success' });
    } catch (err) {
      console.error('Error deleting survey permanently:', err);
      setSnackbar({ open: true, message: t('errorDeletePermanent'), severity: 'error' });
    }
    setDeletePermanentDialog(false);
    setSelectedSurvey(null);
  };

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // ─── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // ─── Main render ───────────────────────────────────────────────────────────
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2.5,
            bgcolor: 'grey.100',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <DeleteSweepIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
              {t('title')}
            </Typography>
            {surveys.length > 0 && (
              <Chip
                label={t('surveysCount', surveys.length)}
                size="small"
                sx={{ fontWeight: 600, bgcolor: 'grey.100', color: 'text.secondary', height: 24 }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.4 }}>
            {t('subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* ── Empty state ── */}
      {surveys.length === 0 ? (
        <Box
          textAlign="center"
          py={10}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
            }}
          >
            <DeleteSweepIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
          </Box>
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
            {t('empty')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('emptySubtitle')}
          </Typography>
        </Box>
      ) : (
        /* ── Survey cards grid ── */
        <Grid container spacing={2.5}>
          {surveys.map((survey) => {
            const questionCount = getQuestionCount(survey.questions);
            return (
              <Grid item xs={12} sm={6} key={survey.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease, background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: 'background.paper',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, pb: 1.5 }}>
                    {/* Title */}
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 0.75, lineHeight: 1.4 }}
                    >
                      {survey.title || t('noTitle')}
                    </Typography>

                    {/* Description */}
                    {survey.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.65,
                        }}
                      >
                        {survey.description}
                      </Typography>
                    )}

                    {/* Meta chips */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: survey.description ? 0 : 1 }}>
                      <Chip
                        icon={<QuestionAnswerIcon sx={{ fontSize: '0.85rem !important' }} />}
                        label={t('questions', questionCount)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', height: 26 }}
                      />
                      <Chip
                        icon={<CalendarTodayIcon sx={{ fontSize: '0.85rem !important' }} />}
                        label={formatDate(survey.createdAt)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', height: 26, color: 'text.secondary' }}
                      />
                    </Box>
                  </CardContent>

                  <Divider />

                  <CardActions sx={{ px: 2, py: 1.25, justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Recover button — prominent */}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<UnarchiveIcon />}
                      onClick={() => handleRecoverClick(survey)}
                      disableElevation
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        px: 2,
                      }}
                    >
                      {t('recover')}
                    </Button>

                    {/* Delete forever — subtle icon button */}
                    <Tooltip title={t('deletePermanently')} arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePermanentClick(survey)}
                        sx={{
                          color: 'text.disabled',
                          transition: 'color 0.2s, transform 0.2s',
                          '&:hover': {
                            color: 'error.main',
                            transform: 'scale(1.15)',
                          },
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Dialog: Recuperar ── */}
      <ConfirmationDialog
        open={recoverDialog}
        onClose={() => { setRecoverDialog(false); setSelectedSurvey(null); }}
        onConfirm={handleRecoverConfirm}
        titleIcon={<UnarchiveIcon color="primary" />}
        titleText={t('confirmRecover')}
        contentText={t('sureRecover', selectedSurvey?.title)}
        cancelText={t('cancel')}
        confirmText={t('recoverBtn')}
        confirmColor="primary"
        confirmIcon={<UnarchiveIcon />}
      />

      {/* ── Dialog: Eliminar definitivamente ── */}
      <ConfirmationDialog
        open={deletePermanentDialog}
        onClose={() => { setDeletePermanentDialog(false); setSelectedSurvey(null); }}
        onConfirm={handleDeletePermanentConfirm}
        titleIcon={<DeleteForeverIcon color="error" />}
        titleText={t('confirmDeletePermanent')}
        contentText={t('sureDeletePermanent', selectedSurvey?.title)}
        cancelText={t('cancel')}
        confirmText={t('deletePermanentBtn')}
        confirmColor="error"
        confirmIcon={<DeleteForeverIcon />}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Papelera;